/**
 * Facet rail + a single accordion column — two panels, not three.
 *
 * `FindingsAccordionList` opens a row's detail directly beneath its own
 * header, so the clicked issue and its evidence sit adjacent rather than in a
 * separate column two panels away. That layout is also what makes the year
 * facet correct: the open row's detail is scoped to the SAME `filter.years`
 * the list above it was narrowed by, so ticking a fiscal year now visibly
 * changes both — before this, the list would narrow while a detail pane
 * beside it kept showing every year regardless, which read as "the filter
 * isn't being applied."
 */

import { useMemo } from 'react';
import type { IssueSeverity, ValidationChain } from '../../../../types/dataQuality';
import FindingsAccordionList from '../FindingsAccordionList';
import FindingsFacetRail from '../FindingsFacetRail';
import { filterIssues, isFilterActive, sortIssues } from '../dqSelectors';
import type { IssueFilter } from '../dqSelectors';
import type { RuleFamily } from '../dqRules';

export interface FindingsViewProps {
  companyId: string;
  chain: ValidationChain;
  filter: IssueFilter;
  selectedCode: string | null;
  onSelectCode: (code: string) => void;
  onCloseDossier: () => void;
  onToggleSeverity: (severity: IssueSeverity) => void;
  onToggleFamily: (family: RuleFamily) => void;
  onToggleYear: (year: number) => void;
  onToggleVerdict: (verdict: string) => void;
  onClearFilter: () => void;
  onSelectField?: (field: string) => void;
}

export default function FindingsView({
  companyId,
  chain,
  filter,
  selectedCode,
  onSelectCode,
  onCloseDossier,
  onToggleSeverity,
  onToggleFamily,
  onToggleYear,
  onToggleVerdict,
  onClearFilter,
  onSelectField,
}: FindingsViewProps) {
  const { summary } = chain;
  // `summary.issues ?? []` is a fresh array every render, which would defeat
  // useMemo below unless it is memoised too.
  const allIssues = useMemo(() => summary.issues ?? [], [summary.issues]);

  const filtered = useMemo(() => sortIssues(filterIssues(allIssues, filter)), [allIssues, filter]);

  /**
   * Does the open finding survive the current filter?
   *
   * With list and detail merged into one accordion, a hidden row simply is
   * not in `filtered`, so nothing stale renders beside it — there is no
   * longer a separate pane to fall out of sync. But silently dropping a
   * finding the reader was actively reading is still the wrong default, so a
   * banner names it and offers the one control that brings it back.
   */
  const hiddenByFilter = useMemo(() => {
    if (!selectedCode) return null;
    if (filtered.some((issue) => issue.code === selectedCode)) return null;
    return allIssues.find((issue) => issue.code === selectedCode) ?? null;
  }, [selectedCode, filtered, allIssues]);

  if (allIssues.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-neutral-200 py-14 text-center">
        <p className="text-sm text-neutral-500">
          No findings were raised for this source — the rule score is final.
        </p>
      </div>
    );
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[220px_1fr] lg:items-start">
      <FindingsFacetRail
        issues={allIssues}
        filter={filter}
        onToggleSeverity={onToggleSeverity}
        onToggleFamily={onToggleFamily}
        onToggleYear={onToggleYear}
        onToggleVerdict={onToggleVerdict}
        onClear={onClearFilter}
        active={isFilterActive(filter)}
      />

      <div className="space-y-3">
        {hiddenByFilter && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-dashed border-neutral-300 bg-neutral-50 px-3 py-2.5">
            <p className="text-xs text-neutral-600">
              The finding you had open,{' '}
              <span className="font-semibold text-neutral-800">{hiddenByFilter.label}</span>, is
              hidden by the current filter.
            </p>
            <button
              type="button"
              onClick={onClearFilter}
              className="shrink-0 rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-xs font-medium text-primary-700 transition-colors hover:border-primary-300 hover:bg-primary-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
            >
              Clear filters
            </button>
          </div>
        )}

        <FindingsAccordionList
          companyId={companyId}
          source={chain.source}
          issues={filtered}
          selectedCode={selectedCode}
          onSelectCode={onSelectCode}
          onCloseDossier={onCloseDossier}
          onSelectField={onSelectField}
          yearFilter={filter.years}
          shownCount={allIssues.length}
          // The backend's true total, which exceeds what it sent only when the
          // digest was actually truncated — `issues_truncated` says so directly.
          totalCount={summary.issue_count ?? allIssues.length}
          truncatedCount={summary.issues_truncated}
        />
      </div>
    </div>
  );
}
